export const vertexShaderSource = `
            attribute vec4 aVertexPosition;
            attribute vec2 aTextureCoord;
            varying vec2 vTextureCoord;

            void main() {
                gl_Position = aVertexPosition;
                vTextureCoord = aTextureCoord;
            }
        `;

export const fragmentShaderSource = `
            precision highp float;
            varying vec2 vTextureCoord;
            uniform float uTime;
            uniform vec2 uResolution;
            uniform sampler2D uImage;

            // Add uniforms for parameters
            uniform float uFlowWeight;
            uniform float uFlockWeight;
            uniform float uChaosWeight;
            uniform float uFlockRadius;
            uniform float uWaveAmplitude;
            uniform float uWaveFrequency;
            uniform float uWaveSpeed;
            uniform float uRotationSpeed;
            uniform float uSortLength;
            uniform float uRandomSwapChance;
            uniform float uBrightnessThreshold;
            uniform float uChaosAmount;
            uniform float uDistortionAmount;

            float random(vec2 st) {
                return fract(sin(dot(st.xy, vec2(12.9898,78.233))) * 43758.5453123);
            }

            float getBrightness(vec4 color) {
                return dot(color.rgb, vec3(0.299, 0.587, 0.114));
            }

            vec4 getPixel(vec2 uv) {
                return texture2D(uImage, clamp(uv, 0.0, 1.0));
            }

            float wave(vec2 p, float time) {
                vec2 pos = p;
                float amplitude = uWaveAmplitude;
                float frequency = uWaveFrequency;
                float phase = time * uWaveSpeed;

                float w = 0.0;

                // Circular wave for global flow
                float dist = length(pos);
                w += sin(dist * 3.0 - phase * 1.5) * amplitude * 0.5;

                // Flowing wave patterns
                w += sin(pos.x * 2.0 + pos.y * 2.0 + phase) * 0.3;
                w += sin(dist * 2.0 - pos.x * 3.0 + time) * 0.2;

                // Add chaos factor based on position
                w += random(pos * 5.0 + time * 0.1) * 0.4;

                return w;
            }

            vec2 getFlowDirection(vec2 uv, float time) {
                float w1 = wave(uv + vec2(0.1), time);
                float w2 = wave(uv - vec2(0.1), time);
                float w3 = wave(uv + vec2(0.0, 0.1), time);
                float w4 = wave(uv - vec2(0.0, 0.1), time);

                vec2 baseFlow = normalize(vec2(w1 - w2, w3 - w4));

                // Flocking behavior - sample neighboring flow
                float flockRadius = uFlockRadius;
                vec2 flockDir = vec2(0.0);

                // Sample 4 neighboring points
                for(int i = 0; i < 4; i++) {
                    float angle = float(i) * 3.14159 * 0.5;
                    vec2 offset = vec2(cos(angle), sin(angle)) * flockRadius;
                    vec2 samplePos = uv + offset;

                    float sw1 = wave(samplePos + vec2(0.1), time);
                    float sw2 = wave(samplePos - vec2(0.1), time);
                    float sw3 = wave(samplePos + vec2(0.0, 0.1), time);
                    float sw4 = wave(samplePos - vec2(0.0, 0.1), time);

                    flockDir += normalize(vec2(sw1 - sw2, sw3 - sw4));
                }
                flockDir = normalize(flockDir);

                // Add per-pixel random deviation
                vec2 randomDir = normalize(vec2(
                    random(uv + vec2(0.0, time)) * 2.0 - 1.0,
                    random(uv + vec2(time, 0.0)) * 2.0 - 1.0
                ));

                // Blend between flow, flocking, and chaos
                vec2 finalDir = normalize(
                    baseFlow * uFlowWeight +
                    flockDir * uFlockWeight +
                    randomDir * uChaosWeight
                );

                return finalDir;
            }

            vec4 pixelSort(vec2 uv, float time) {
                vec2 flowDir = getFlowDirection(uv, time);
                float waveVal = wave(uv, time);

                // Dynamic sort length based on flow coherence
                float sortLength = uSortLength + random(uv + time * 0.2) * 0.15;
                sortLength *= (1.0 + sin(waveVal * 3.0) * 0.3); // Vary by wave pattern

                int steps = 85;

                // Reduced rotation for more independent movement
                float angle = time * uRotationSpeed + waveVal * 0.3;
                mat2 rotation = mat2(cos(angle), -sin(angle), sin(angle), cos(angle));
                flowDir = rotation * flowDir;

                vec4 currentColor = getPixel(uv);
                float currentBrightness = getBrightness(currentColor);

                // Adaptive threshold based on local flow coherence
                float threshold = uBrightnessThreshold + random(uv + time) * 0.1;
                threshold *= (1.0 + waveVal * 0.3);

                for(int i = 0; i < 25; i++) {
                    if(float(i) >= float(steps)) break;

                    float t = float(i) / float(steps);
                    t = t * t * (3.0 - 2.0 * t);
                    t += random(uv + time + float(i)) * 0.2;

                    // Blend between flow direction and random movement
                    vec2 randomOffset = normalize(vec2(
                        random(uv + time + float(i)) * 2.0 - 1.0,
                        random(uv + time + float(i) + 1.0) * 2.0 - 1.0
                    ));

                    float flowInfluence = smoothstep(0.0, 1.0, sin(time * 0.5 + waveVal) * 0.5 + 0.5);
                    vec2 stepDir = mix(randomOffset, flowDir, flowInfluence);

                    vec2 offset = normalize(stepDir) * (t * sortLength);
                    vec2 sampleUV = uv + offset;

                    vec4 sampleColor = getPixel(sampleUV);
                    float sampleBrightness = getBrightness(sampleColor);

                    if(sampleBrightness < currentBrightness - threshold ||
                       random(uv + time + float(i)) > uRandomSwapChance) {
                        currentColor = sampleColor;
                        currentBrightness = sampleBrightness;
                    }
                }

                return currentColor;
            }

            void main() {
                vec2 uv = vTextureCoord;

                // Add slight UV distortion for more chaos
                uv += (vec2(
                    random(uv + uTime * 0.1),
                    random(uv + uTime * 0.2)
                ) * 2.0 - 1.0) * uDistortionAmount;

                vec4 color = pixelSort(uv, uTime);
                gl_FragColor = color;
            }
        `;
